import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, RotateCcw, Heart, Scale, Shield, Lightbulb, GitBranch, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const frameworkMeta = {
  "10-10-10": { 
    icon: Clock,
    title: "10-10-10 Rule", 
    desc: "How will this feel across time?",
    detail: "Evaluate at 10 minutes, 10 months, 10 years"
  },
  "inversion": { 
    icon: RotateCcw,
    title: "Inversion Thinking", 
    desc: "What would make this fail?",
    detail: "Identify failure modes, then prevent them"
  },
  "regret_minimization": { 
    icon: Heart,
    title: "Regret Minimization", 
    desc: "What would 80-year-old you choose?",
    detail: "Minimize the regrets that matter most"
  },
  "opportunity_cost": {
    icon: Scale,
    title: "Opportunity Cost",
    desc: "What do you give up with each choice?",
    detail: "Evaluate trade-offs explicitly"
  },
  "risk_reversibility": {
    icon: Shield,
    title: "Risk vs Reversibility",
    desc: "How bad is the downside? Can you undo it?",
    detail: "Assess risk against ability to reverse"
  },
  "first_principles": {
    icon: Lightbulb,
    title: "First Principles",
    desc: "What are the fundamental truths?",
    detail: "Strip assumptions, rebuild from facts"
  },
  "second_order": {
    icon: GitBranch,
    title: "Second-Order Effects",
    desc: "What happens after the first impact?",
    detail: "Analyze downstream consequences"
  },
  "worst_best_case": {
    icon: Target,
    title: "Worst/Best Case Mapping",
    desc: "What are the realistic extremes?",
    detail: "Map the full decision space"
  }
};

export default function FrameworkSelectPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [frameworksRes, sessionRes] = await Promise.all([
          axios.get(`${API}/frameworks`),
          axios.get(`${API}/decision/${sessionId}`)
        ]);
        setFrameworks(frameworksRes.data.frameworks);
        setSession(sessionRes.data);
      } catch {
        toast.error("Session not found");
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [sessionId, navigate]);

  const handleSelectFramework = async () => {
    if (!selectedFramework) {
      toast.error("Select a framework");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/decision/${sessionId}/framework`, {
        framework: selectedFramework
      });
      navigate(`/think/${sessionId}`);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/decide")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-foreground">Step 2 of 4</span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl mx-auto space-y-8"
        >
          {/* Title */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-primary uppercase tracking-wider">
              Thinking Framework
            </p>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground">
              How do you want to analyze this?
            </h1>
          </div>

          {/* Decision preview */}
          {session && (
            <div className="p-4 rounded-lg bg-secondary/50 border border-border/50">
              <p className="text-sm text-foreground">{session.decision_text}</p>
            </div>
          )}

          {/* Framework options - 2 column grid */}
          <div className="grid md:grid-cols-2 gap-3">
            {frameworks.map((framework, index) => {
              const meta = frameworkMeta[framework.id];
              const isSelected = selectedFramework === framework.id;
              const Icon = meta?.icon || Clock;
              
              return (
                <motion.button
                  key={framework.id}
                  data-testid={`framework-${framework.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  onClick={() => setSelectedFramework(framework.id)}
                  className={`p-4 rounded-xl text-left transition-all ${
                    isSelected 
                      ? 'glow-card' 
                      : 'floating-card hover:border-border'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-primary/20' : 'bg-secondary'
                    }`}>
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-medium text-sm text-foreground">
                          {meta?.title || framework.name}
                        </h3>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {meta?.desc || framework.description}
                      </p>
                      <p className="text-[11px] text-muted-foreground/70">
                        {meta?.detail}
                      </p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-4">
            <Button
              data-testid="continue-thinking-btn"
              onClick={handleSelectFramework}
              disabled={isSubmitting || !selectedFramework}
              className="h-11 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm shadow-lg shadow-primary/20"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Begin thinking
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Disclaimer Footer */}
      <footer className="py-6 px-6">
        <p className="text-[11px] text-muted-foreground/50 text-center">
          Decisora provides structured thinking tools, not professional advice.
        </p>
      </footer>
    </div>
  );
}
